// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (API + labels for the fleet screens: pages/admin/AdminDrivers, AdminDriverKyc, AdminDriverDetail, AdminDispatch)
// Typed wrappers over the staff fleet endpoints (GET/POST /admin/drivers, /admin/drivers/{id}/…, GET /admin/dispatch, PUT /admin/orders/{ref}/driver).
// Reads work for any staff role; writes need admin/editor (the backend enforces it, the UI only hides what a role can't do). KYC is staff bookkeeping:
// the checks are attestations, never copies of documents, and a re-upload request is only recorded — nothing is sent to the captain.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";
import type { Order } from "./orders";

export type Duty = "off_duty" | "standby" | "on_shift";
export type Kyc = "pending" | "approved" | "rejected";
export type Vehicle = "ev_scooter" | "cold_van" | "crate_scooter" | "other";
export type CheckName = "identity" | "licence" | "vehicleRegistration" | "foodSafety" | "background";

export const DUTY_LABEL: Record<Duty, string> = { off_duty: "Off duty", standby: "Standby at hub", on_shift: "On shift" };
export const KYC_LABEL: Record<Kyc, string> = { pending: "Verification pending", approved: "Verified", rejected: "Rejected" };
export const VEHICLE_LABEL: Record<Vehicle, string> = { ev_scooter: "EV scooter", cold_van: "Insulated cold-van", crate_scooter: "Chilled-crate scooter", other: "Other vehicle" };
export const VEHICLES = Object.keys(VEHICLE_LABEL) as Vehicle[];
/** The checks staff attest to, in the order they are shown; the first three are needed for approval. */
export const CHECKS: { id: CheckName; label: string; required: boolean }[] = [
  { id: "identity", label: "Identity document", required: true },
  { id: "licence", label: "Driving licence", required: true },
  { id: "vehicleRegistration", label: "Vehicle registration", required: true },
  { id: "foodSafety", label: "Food-safety certificate", required: false },
  { id: "background", label: "Background check", required: false },
];

export interface Driver {
  id: string;
  name: string;
  phone: string;
  vehicleType: Vehicle;
  vehicleNumber: string | null;
  hub: string | null;
  duty: Duty;
  kyc: Kyc;
  suspended: boolean;
  /** Approved, not suspended, and on standby / on shift: can be given an order. */
  available: boolean;
  kycChecks: Record<CheckName, boolean>;
  kycNote: string | null;
  kycDecidedBy: string | null;
  kycDecidedAt: string | null;
  reuploadDocuments: CheckName[];
  reuploadNote: string | null;
  reuploadRequestedAt: string | null;
  opsNote: string | null;
  opsNoteBy: string | null;
  opsNoteAt: string | null;
  createdAt: string;
}
export interface DriverRow {
  driver: Driver;
  /** Handed to them and not yet delivered / cancelled. */
  activeOrders: number;
  delivered: number;
  lastDeliveryAt: string | null;
}
export interface DriverDetail {
  row: DriverRow;
  /** Everything handed to this captain, newest first. */
  orders: Order[];
}
export interface DispatchOrder {
  order: Order;
  driverId: string | null;
  driverName: string | null;
}
export interface Dispatch {
  orders: DispatchOrder[];
  drivers: DriverRow[];
}
export interface DriverInput {
  name: string;
  phone: string;
  vehicleType: Vehicle;
  vehicleNumber: string;
  hub: string;
}

const base = (id: string) => `/admin/drivers/${encodeURIComponent(id)}`;
export const fetchDrivers = () => apiAuthed<{ drivers: DriverRow[] }>("GET", "/admin/drivers");
export const createDriver = (input: DriverInput) => apiAuthed<DriverDetail>("POST", "/admin/drivers", input);
export const fetchDriver = (id: string) => apiAuthed<DriverDetail>("GET", base(id));
export const setDuty = (id: string, duty: Duty) => apiAuthed<DriverDetail>("PUT", `${base(id)}/duty`, { duty });
export const setSuspension = (id: string, suspended: boolean) => apiAuthed<DriverDetail>("PUT", `${base(id)}/suspension`, { suspended });
export const saveKyc = (id: string, checks: Partial<Record<CheckName, boolean>>, note: string) => apiAuthed<DriverDetail>("PUT", `${base(id)}/kyc`, { checks, note });
export const decideKyc = (id: string, decision: "approved" | "rejected", note: string) => apiAuthed<DriverDetail>("POST", `${base(id)}/kyc/decision`, { decision, note });
export const requestReupload = (id: string, documents: CheckName[], note: string) => apiAuthed<DriverDetail>("POST", `${base(id)}/kyc/reupload`, { documents, note });
export const saveDriverNote = (id: string, note: string) => apiAuthed<DriverDetail>("PUT", `${base(id)}/note`, { note });
export const fetchDispatch = () => apiAuthed<Dispatch>("GET", "/admin/dispatch");
export const assignDriver = (ref: string, driverId: string | null) => apiAuthed<DispatchOrder>("PUT", `/admin/orders/${encodeURIComponent(ref)}/driver`, { driverId });

/** Helpers shared by the fleet screens. */
export const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
export const shortId = (id: string) => `#${id.slice(-6).toUpperCase()}`;
export const digitsOf = (phone: string) => phone.replace(/\D/g, "");
export const canWriteFleet = (role: string | null | undefined) => role === "admin" || role === "editor";
